import { TypeContrato } from '@/features/agreement/utils/enum'
import { produtosLabelsTypeContract } from '@/features/products/utils'


export function RemovaCharEspeciais(valor: string | undefined): string {
  if (!valor) {
    return '' // Retorna uma string vazia se o valor for undefined ou null
  }

  // Remove caracteres especiais
  const semCharEspeciais = valor.replace(/[^\w\s]/gi, '')

  // Remove espaços à esquerda do primeiro número
  return semCharEspeciais.replace(/\s+(?=\d)/, '')
}

// utils/formata.ts
export function formatNome(value: unknown): string {
  if (typeof value !== 'string') return '' // evita quebrar
  const s = value.trim()
  if (!s) return ''
  const labels: Record<string, string> = {
    GERENTE: 'Parceiro Gestor',
    PARCEIRO: 'Parceiro Comercial'
  }
  if (labels[s]) return labels[s]
  // Title Case simples, mantendo preposições minúsculas (exceto a primeira palavra)
  const preps = new Set(['de', 'do', 'da', 'dos', 'das', 'e'])
  return s
    .split(/\s+/)
    .map((w, i) => {
      const low = w.toLowerCase()
      if (i > 0 && preps.has(low)) return low
      return low.charAt(0).toUpperCase() + low.slice(1)
    })
    .join(' ')
}

export function hasNumber(value: unknown): boolean {
  return typeof value === 'string' && /\d/.test(value)
}

export function isValidPersonName(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0 && !hasNumber(value)
}

export function formatRegimeTitle(input: string): string {
  if (!input) return '—'

  // tenta normalizar "Simples Nacional" -> "SIMPLES_NACIONAL"
  const normalizedKey = input
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos
    .replace(/[^A-Za-z0-9]+/g, '_') // vira snake
    .replace(/^_+|_+$/g, '')
    .toUpperCase()

  // se existir no enum, usa; senão, mantém a key normalizada como tentativa
  const enumKey = (TypeContrato as any)[normalizedKey] ?? normalizedKey

  // tenta mapear no dicionário de labels; se não achar, retorna o original
  return produtosLabelsTypeContract[enumKey as TypeContrato] ?? input
}

export function pegarPrimeiroNome(nomeCompleto: string): string {
  const nomeFormatado = formatNome(nomeCompleto)
  const [primeiroNome] = nomeFormatado.split(' ')
  return primeiroNome
}

export function pegarPrimeiroEUltimoNome(nomeCompleto: string): string {
  const nomeFormatado = formatNome(nomeCompleto)
  const nomes = nomeFormatado.trim().split(/\s+/)

  if (nomes.length === 0) return ''

  if (nomes.length === 1) return nomes[0] // só um nome

  const primeiroNome = nomes[0]
  const ultimoNome = nomes[nomes.length - 1]

  return `${primeiroNome} ${ultimoNome}`
}

export function limparMascara(valor?: string | null): string {
  if (!valor) return ''
  return valor.replace(/[^\d]/g, '')
}

export const formatCNPJ = (value: string) => {
  const onlyNumbers = value.replace(/\D/g, '') // Remove tudo que não é número
  return onlyNumbers
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
    .slice(0, 18) // Limita o tamanho ao de um CNPJ
}

export const formatCEP = (value: string) => {
  const onlyNumbers = value.replace(/\D/g, '') // Remove tudo que não é número
  return onlyNumbers.replace(/^(\d{5})(\d)/, '$1-$2').slice(0, 9) // Formato 00000-000
}

export const formatDate = (dateInput: Date | string | null | undefined) => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
  if (!date || isNaN(date.getTime())) return 'Data inválida'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date)
}
export const formatMonthYear = (input: Date | string | null | undefined): string => {
  if (!input) return 'Data inválida'

  // Caso seja um Date válido
  if (input instanceof Date && !isNaN(input.getTime())) {
    return new Intl.DateTimeFormat('pt-BR', {
      month: '2-digit',
      year: 'numeric'
    }).format(input)
  }

  // Caso seja string
  if (typeof input === 'string') {
    let cleaned = input.replace(/\D/g, '')

    // Formatos possíveis:
    // 1) "052022" → MMYYYY (6 dígitos)
    // 2) "05-2022", "05/2022" → vira "052022"

    if (cleaned.length === 6) {
      const mm = cleaned.slice(0, 2)
      const yyyy = cleaned.slice(2, 6)

      const month = Number(mm)
      const year = Number(yyyy)

      if (month >= 1 && month <= 12 && year > 1900)
        return `${mm}/${yyyy}`
    }

    // Tentar parsear strings com data completa (ex: "2022-05-01")
    const parsed = new Date(input)
    if (!isNaN(parsed.getTime())) {
      const mm = String(parsed.getMonth() + 1).padStart(2, '0')
      const yyyy = parsed.getFullYear()
      return `${mm}/${yyyy}`
    }
  }

  return 'Data inválida'
}


export const formatSingularOrPlural = (singular: string, plural: string, qtd: number): string => {
  if (qtd > 1) {
    return plural
  }

  return singular
}

export function formatMoeda(valor: number | string | undefined) {
  const n = typeof valor === 'number' ? valor : parseFloat(valor || '0')
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })
}
export function formatValor(valor: number | string | undefined): string {
  const numero = typeof valor === 'number' ? valor : parseFloat(valor || '0')
  const parts = numero.toFixed(2).split('.')
  return `R$ ${parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${parts[1]}`
}
export function formatCompetencia(competencia: string | undefined): string {
  if (!competencia) return ''
  const clean = competencia.replace(/[-/]/g, '-') // normaliza separador
  const [ano, mes] = clean.split('-')
  return `${mes}/${ano}`
}
